import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { SuperAdminAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';


const SETTING_KEYS = [
  'honeypot_enabled',
  'timing_check_enabled',
  'timing_min_seconds',
  'timing_allow_first_quick_login',
  'ip_rate_limit_enabled',
  'ip_max_attempts',
  'ip_window_minutes',
  'behind_proxy',
  'account_lockout_enabled',
  'account_max_failed_attempts',
  'account_lock_minutes',
  'device_limit_enabled',
  'device_max_accounts',
  'device_window_minutes',
  'device_missing_id_policy',
  'sharing_flag_enabled',
  'sharing_flag_devices_per_day',
];

const LAYERS = [
  { id: 'honeypot', flag: 'honeypot_enabled', label: 'Honeypot' },
  { id: 'timing', flag: 'timing_check_enabled', label: 'Form timing' },
  { id: 'ip_rate_limit', flag: 'ip_rate_limit_enabled', label: 'IP rate limit' },
  { id: 'account_lockout', flag: 'account_lockout_enabled', label: 'Account lockout' },
  { id: 'device_limit', flag: 'device_limit_enabled', label: 'Device account limit' },
  { id: 'sharing_flag', flag: 'sharing_flag_enabled', label: 'Sharing flags' },
];


function payloadFromForm(form) {
  const body = {};
  SETTING_KEYS.forEach((key) => {
    if (form[key] !== undefined) body[key] = form[key];
  });
  return body;
}


function Toggle({ id, label, help, checked, onChange, disabled }) {
  return (
    <label
      htmlFor={id}
      className="ty-row"
      style={{ gap: 8, alignItems: 'flex-start', cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <input
        id={id}
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 4 }}
      />
      <span>
        <strong>{label}</strong>
        {help && (
          <span style={{ display: 'block', fontSize: 13, color: 'var(--ty-muted)', marginTop: 2 }}>
            {help}
          </span>
        )}
      </span>
    </label>
  );
}


function NumberField({ id, label, help, value, min, max, disabled, onChange }) {
  return (
    <div className="ty-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        className="ty-input"
        min={min}
        max={max}
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => {
          if (e.target.value === '') return;
          const n = Number(e.target.value);
          if (Number.isInteger(n)) onChange(n);
        }}
        style={disabled ? { background: '#f3f4f6' } : undefined}
      />
      {help && (
        <span style={{ fontSize: 12, color: 'var(--ty-muted)' }}>{help}</span>
      )}
    </div>
  );
}


function lockSubject(lock) {
  if (!lock) return '—';
  if (lock.kind === 'account') {
    return lock.schema_name
      ? `${lock.username || lock.subject} (${lock.schema_name})`
      : (lock.username || lock.subject || '—');
  }
  if (lock.kind === 'ip') return lock.ip || lock.subject || '—';
  if (lock.kind === 'device') {
    const id = lock.device_id || lock.subject || '—';
    return lock.account_count ? `${id} · ${lock.account_count} accounts` : id;
  }
  return lock.subject || '—';
}

function eventSubject(event) {
  const parts = [];
  if (event?.username) parts.push(event.username);
  if (event?.ip) parts.push(event.ip);
  return parts.join(' · ') || '—';
}


function LayerCard({ title, children }) {
  return (
    <section className="ty-card" style={{ marginTop: 14 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 14px' }}>{title}</h2>
      {children}
    </section>
  );
}


/**
 * Super Admin → Bot Security.
 *
 * Site superusers configure the server-side login-security policy
 * (honeypot, timing, IP rate limit, lockout, device cap, sharing flags).
 * Organisation admins never see this page: the module is gated by
 * ``requireSuperAdmin`` and the API returns 403 unless ``is_superuser``.
 */
export default function LoginSecurityConfig() {
  const [form, setForm] = useState(null);
  const [locks, setLocks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key) => (value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyPayload = (payload) => {
    setForm(payload);
    setLocks(Array.isArray(payload?.active_locks) ? payload.active_locks : []);
    setEvents(Array.isArray(payload?.recent_events) ? payload.recent_events : []);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await SuperAdminAPI.loginSecurity.get();
      applyPayload(payload);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const save = useCallback(async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      const payload = await SuperAdminAPI.loginSecurity.save(payloadFromForm(form));
      applyPayload(payload);
      notify.success('Login security configuration saved.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [form]);

  if (loading && !form) return <Loader label="Loading login security…" />;

  const timingOff = !form?.timing_check_enabled;
  const ipOff = !form?.ip_rate_limit_enabled;
  const lockOff = !form?.account_lockout_enabled;
  const deviceOff = !form?.device_limit_enabled;
  const shareOff = !form?.sharing_flag_enabled;

  return (
    <>
      <PageHeader
        title="🛡️ Bot Security"
        subtitle="Login security configuration for every login (HTML, React, and mobile). Organisation admins cannot change this policy."
        actions={
          <>
            <button
              type="button"
              className="ty-btn ty-btn--ghost"
              onClick={reload}
              disabled={loading || saving}
            >
              ↻ Refresh
            </button>
            <button
              type="button"
              className="ty-btn"
              onClick={save}
              disabled={saving || !form}
            >
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </>
        }
      />

      <ErrorBox error={error} />

      {form && (
        <>
          <div className="ty-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <strong style={{ marginRight: 4 }}>Layer status:</strong>
            {LAYERS.map((layer) => {
              const active = !!form[layer.flag];
              return (
                <span
                  key={layer.id}
                  className="ty-badge"
                  style={{
                    backgroundColor: active ? 'rgba(95, 215, 136, 0.18)' : '#eceff1',
                    color: active ? '#1b7a45' : '#607d8b',
                    borderColor: active ? 'rgba(95, 215, 136, 0.45)' : '#cfd8dc',
                  }}
                >
                  {layer.label} {active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              );
            })}
          </div>

          <section className="ty-card" style={{ marginTop: 14 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
              Currently locked accounts &amp; devices
            </h2>
            <p style={{ fontSize: 13, color: 'var(--ty-muted)', margin: '0 0 12px' }}>
              When a login is blocked by a security layer, the reason is listed here
              and shown on the login screen. Locks expire when the configured window ends.
            </p>
            {locks.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--ty-muted)' }}>
                No accounts, IPs, or devices are currently locked.
              </p>
            ) : (
              <div className="ty-table-wrap">
                <table className="ty-table">
                  <thead>
                    <tr>
                      <th>Lock</th>
                      <th>Who / what</th>
                      <th>Reason</th>
                      <th>Unlocks in</th>
                      <th>Layer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locks.map((lock, i) => (
                      <tr key={`${lock.kind}-${lock.subject}-${i}`}>
                        <td>{lock.title || 'Locked'}</td>
                        <td>{lockSubject(lock)}</td>
                        <td>{lock.lock_reason || '—'}</td>
                        <td>{lock.retry_after_label || '—'}</td>
                        <td>{lock.layer_label || lock.layer || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="ty-card" style={{ marginTop: 14 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px' }}>
              Recent login blocks
            </h2>
            {events.length === 0 ? (
              <p style={{ margin: 0, color: 'var(--ty-muted)' }}>
                No recent login blocks recorded.
              </p>
            ) : (
              <div className="ty-table-wrap">
                <table className="ty-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Layer</th>
                      <th>Reason</th>
                      <th>Account / IP</th>
                      <th>Message shown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event, i) => (
                      <tr key={`${event.created_at}-${event.layer}-${i}`}>
                        <td>{event.created_at || '—'}</td>
                        <td>{event.layer_label || event.layer || '—'}</td>
                        <td>{event.lock_reason || '—'}</td>
                        <td>{eventSubject(event)}</td>
                        <td>{event.message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <form
            onSubmit={(e) => { e.preventDefault(); save(); }}
          >
            <LayerCard title="Layer 1 — Honeypot field">
              <Toggle
                id="honeypot_enabled"
                label="Enable honeypot"
                help="Hidden website_url field. Bots that fill it are rejected. Not rendered when off."
                checked={form.honeypot_enabled}
                onChange={set('honeypot_enabled')}
                disabled={saving}
              />
            </LayerCard>

            <LayerCard title="Layer 2 — Form timing check">
              <Toggle
                id="timing_check_enabled"
                label="Enable timing check"
                help="Rejects submissions faster than the HMAC-signed timestamp allows."
                checked={form.timing_check_enabled}
                onChange={set('timing_check_enabled')}
                disabled={saving}
              />
              <div className="ty-grid-2" style={{ marginTop: 12 }}>
                <NumberField
                  id="timing_min_seconds"
                  label="Minimum seconds"
                  help="Rejects submissions faster than this (1–60)."
                  value={form.timing_min_seconds}
                  min={1}
                  max={60}
                  disabled={saving || timingOff}
                  onChange={set('timing_min_seconds')}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <Toggle
                  id="timing_allow_first_quick_login"
                  label="Allow first-time quick login"
                  help="Sign in immediately on the first attempt (autofill, App Store review, and mobile). The HMAC token is still required. Turn this off to require the minimum wait on HTML logins."
                  checked={form.timing_allow_first_quick_login}
                  onChange={set('timing_allow_first_quick_login')}
                  disabled={saving || timingOff}
                />
              </div>
            </LayerCard>

            <LayerCard title="Layer 3 — IP-based rate limiting">
              <Toggle
                id="ip_rate_limit_enabled"
                label="Enable IP rate limit"
                help="Counts login attempts per client IP in a sliding window."
                checked={form.ip_rate_limit_enabled}
                onChange={set('ip_rate_limit_enabled')}
                disabled={saving}
              />
              <div className="ty-grid-2" style={{ marginTop: 12 }}>
                <NumberField
                  id="ip_max_attempts"
                  label="Max attempts"
                  help="Login attempts per IP per window (1–100)."
                  value={form.ip_max_attempts}
                  min={1}
                  max={100}
                  disabled={saving || ipOff}
                  onChange={set('ip_max_attempts')}
                />
                <NumberField
                  id="ip_window_minutes"
                  label="Window (minutes)"
                  help="Sliding window length (1–1440)."
                  value={form.ip_window_minutes}
                  min={1}
                  max={1440}
                  disabled={saving || ipOff}
                  onChange={set('ip_window_minutes')}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <Toggle
                  id="behind_proxy"
                  label="Behind proxy"
                  help="Read client IP from X-Forwarded-For only when this is on."
                  checked={form.behind_proxy}
                  onChange={set('behind_proxy')}
                  disabled={saving || ipOff}
                />
              </div>
            </LayerCard>

            <LayerCard title="Layer 4 — Per-account failed-attempt lockout">
              <Toggle
                id="account_lockout_enabled"
                label="Enable account lockout"
                help="Temporarily locks an account after too many wrong passwords."
                checked={form.account_lockout_enabled}
                onChange={set('account_lockout_enabled')}
                disabled={saving}
              />
              <div className="ty-grid-2" style={{ marginTop: 12 }}>
                <NumberField
                  id="account_max_failed_attempts"
                  label="Max failed attempts"
                  help="Wrong passwords before a temporary lock (1–100)."
                  value={form.account_max_failed_attempts}
                  min={1}
                  max={100}
                  disabled={saving || lockOff}
                  onChange={set('account_max_failed_attempts')}
                />
                <NumberField
                  id="account_lock_minutes"
                  label="Lock duration (minutes)"
                  help="Auto-unlocks when the Redis TTL expires (1–1440)."
                  value={form.account_lock_minutes}
                  min={1}
                  max={1440}
                  disabled={saving || lockOff}
                  onChange={set('account_lock_minutes')}
                />
              </div>
            </LayerCard>

            <LayerCard title="Layer 5 — Device-based account limit">
              <Toggle
                id="device_limit_enabled"
                label="Enable device limit"
                help="Caps how many distinct accounts can log in from one device in a fixed window."
                checked={form.device_limit_enabled}
                onChange={set('device_limit_enabled')}
                disabled={saving}
              />
              <div className="ty-grid-2" style={{ marginTop: 12 }}>
                <NumberField
                  id="device_max_accounts"
                  label="Max accounts per device"
                  help="Distinct users allowed on one device in the window (1–100)."
                  value={form.device_max_accounts}
                  min={1}
                  max={100}
                  disabled={saving || deviceOff}
                  onChange={set('device_max_accounts')}
                />
                <NumberField
                  id="device_window_minutes"
                  label="Window (minutes)"
                  help="Fixed window — TTL is set once, not extended on login (1–1440)."
                  value={form.device_window_minutes}
                  min={1}
                  max={1440}
                  disabled={saving || deviceOff}
                  onChange={set('device_window_minutes')}
                />
                <div className="ty-field">
                  <label htmlFor="device_missing_id_policy">Missing device ID</label>
                  <select
                    id="device_missing_id_policy"
                    className="ty-select"
                    value={form.device_missing_id_policy || 'block'}
                    disabled={saving || deviceOff}
                    onChange={(e) => set('device_missing_id_policy')(e.target.value)}
                    style={deviceOff ? { background: '#f3f4f6' } : undefined}
                  >
                    <option value="block">Block</option>
                    <option value="allow">Allow</option>
                  </select>
                  <span style={{ fontSize: 12, color: 'var(--ty-muted)' }}>
                    Block still enforces the account cap using IP + browser when
                    device_id is missing. Allow skips the device limit for that request.
                  </span>
                </div>
              </div>
            </LayerCard>

            <LayerCard title="Layer 6 — Account-sharing flags (monitoring only)">
              <Toggle
                id="sharing_flag_enabled"
                label="Enable sharing flags"
                help="If an account is seen on more devices in 24h, a flag is stored. Login is never blocked."
                checked={form.sharing_flag_enabled}
                onChange={set('sharing_flag_enabled')}
                disabled={saving}
              />
              <div className="ty-grid-2" style={{ marginTop: 12 }}>
                <NumberField
                  id="sharing_flag_devices_per_day"
                  label="Devices per day"
                  help="Threshold for writing a sharing flag (1–100). Never blocks login."
                  value={form.sharing_flag_devices_per_day}
                  min={1}
                  max={100}
                  disabled={saving || shareOff}
                  onChange={set('sharing_flag_devices_per_day')}
                />
              </div>
            </LayerCard>

            <div className="ty-row ty-row--end" style={{ marginTop: 16 }}>
              <button type="submit" className="ty-btn" disabled={saving}>
                {saving ? 'Saving…' : 'Save Configuration'}
              </button>
            </div>
          </form>
        </>
      )}
    </>
  );
}
